import React, { useState, useMemo } from 'react';
import { useSearch } from '@/hooks/useSearch';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Star, ArrowUpDown } from 'lucide-react';

type SortKey = 'name' | 'rating' | 'price';

export const ComparisonTable: React.FC = () => {
  const { filteredBots } = useSearch();
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortKey, setSortKey] = useState<SortKey>('rating');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const categories = useMemo(() => {
    const cats = new Set(filteredBots.map(b => b.category).filter(Boolean));
    return ['all', ...Array.from(cats)];
  }, [filteredBots]);

  const filtered = useMemo(() => {
    let bots = filteredBots;
    if (categoryFilter !== 'all') {
      bots = bots.filter(b => b.category === categoryFilter);
    }
    bots = [...bots].sort((a, b) => {
      if (sortKey === 'name') return sortDir === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
      if (sortKey === 'rating') return sortDir === 'asc' ? a.rating - b.rating : b.rating - a.rating;
      if (sortKey === 'price') {
        const order = { Free: 0, Freemium: 1, Paid: 2 };
        return sortDir === 'asc' ? (order[a.price] || 0) - (order[b.price] || 0) : (order[b.price] || 0) - (order[a.price] || 0);
      }
      return 0;
    });
    return bots.slice(0, 50);
  }, [filteredBots, categoryFilter, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  return (
    <section className="py-12 px-4">
      <div className="container mx-auto max-w-6xl">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <h2 className="text-2xl font-bold">All AI Tools</h2>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filter by category" />
            </SelectTrigger>
            <SelectContent>
              {categories.map(c => (
                <SelectItem key={c} value={c}>{c === 'all' ? 'All Categories' : c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="overflow-x-auto border border-border rounded-lg">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="text-left p-3 font-medium">
                  <Button variant="ghost" size="sm" className="gap-1 -ml-2" onClick={() => toggleSort('name')}>
                    Tool <ArrowUpDown className="w-3 h-3" />
                  </Button>
                </th>
                <th className="text-left p-3 font-medium">Category</th>
                <th className="text-left p-3 font-medium">
                  <Button variant="ghost" size="sm" className="gap-1 -ml-2" onClick={() => toggleSort('price')}>
                    Pricing <ArrowUpDown className="w-3 h-3" />
                  </Button>
                </th>
                <th className="text-left p-3 font-medium">Free Tier</th>
                <th className="text-left p-3 font-medium">
                  <Button variant="ghost" size="sm" className="gap-1 -ml-2" onClick={() => toggleSort('rating')}>
                    Rating <ArrowUpDown className="w-3 h-3" />
                  </Button>
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(bot => (
                <tr key={bot.id} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
                  <td className="p-3 font-medium">{bot.name}</td>
                  <td className="p-3 text-muted-foreground">{bot.category}</td>
                  <td className="p-3">
                    <Badge variant="outline" className="text-xs">{bot.price}</Badge>
                  </td>
                  <td className="p-3">{bot.price === 'Free' || bot.price === 'Freemium' ? 'Yes' : 'No'}</td>
                  <td className="p-3">
                    <span className="flex items-center gap-1">
                      <Star className="w-3.5 h-3.5 fill-foreground text-foreground" />
                      {bot.rating}
                    </span>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">No tools found for this category.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};
